import React, { useEffect, useRef } from 'react'
import { X, Terminal, Eye, Activity, Download, Trash2 } from 'lucide-react'
import { ExecutionLog } from '../../stores/workflowStore'

interface MissionControlProps {
  isVisible: boolean
  onClose: () => void
  logs: ExecutionLog[]
  isExecuting: boolean
}

const MissionControl: React.FC<MissionControlProps> = ({
  isVisible,
  onClose,
  logs,
  isExecuting
}) => {
  const logsEndRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = React.useState(true)
  const [logFilter, setLogFilter] = React.useState<'all' | 'info' | 'warning' | 'error' | 'debug'>('all')

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [logs, autoScroll])

  const filteredLogs = logs.filter(log => 
    logFilter === 'all' || log.level === logFilter
  )

  const getLogColor = (level: string) => {
    switch (level) {
      case 'info': return 'text-green-400'
      case 'warning': return 'text-yellow-400'
      case 'error': return 'text-red-400'
      case 'debug': return 'text-blue-400'
      default: return 'text-white'
    }
  }

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const exportLogs = () => {
    const logText = logs.map(log => 
      `[${formatTimestamp(log.timestamp)}] ${log.level.toUpperCase()}: ${log.message}`
    ).join('\n')
    
    const blob = new Blob([logText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `workflow-logs-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!isVisible) return null

  return (
    <div className="w-96 sidebar border-l flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Mission Control
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Status */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Workflow Status
            </span>
            <div className={`flex items-center ${isExecuting ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
              <div className={`status-indicator mr-2 ${isExecuting ? 'status-running' : 'status-completed'}`}></div>
              <span className="text-sm font-medium">
                {isExecuting ? 'Executing' : 'Completed'}
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-blue-900 dark:bg-blue-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((logs.length / 15) * 100, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>{logs.length} operations completed</span>
            <span>{Math.min((logs.length / 15) * 100, 100).toFixed(0)}%</span>
          </div>
        </div>
        
        {/* Agent Status Cards */}
        <div className="space-y-3 mb-6">
          {[
            { name: 'Research Agent', status: logs.length > 5 ? 'completed' : logs.length > 2 ? 'running' : 'waiting', icon: '🔍' },
            { name: 'Writer Agent', status: logs.length > 10 ? 'completed' : logs.length > 7 ? 'running' : 'waiting', icon: '✍️' },
            { name: 'Analysis Task', status: logs.length > 13 ? 'completed' : logs.length > 11 ? 'running' : 'waiting', icon: '📊' }
          ].map((agent, index) => (
            <div key={index} className={`card p-3 ${agent.status === 'running' ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 ${
                    agent.status === 'completed' ? 'bg-green-100 dark:bg-green-900/20' :
                    agent.status === 'running' ? 'bg-blue-100 dark:bg-blue-900/20' :
                    'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    {agent.status === 'completed' ? (
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    ) : agent.status === 'running' ? (
                      <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {agent.name}
                  </span>
                </div>
                <span className={`text-xs font-medium ${
                  agent.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                  agent.status === 'running' ? 'text-blue-600 dark:text-blue-400' :
                  'text-gray-500 dark:text-gray-400'
                }`}>
                  {agent.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Live Logs Section */}
      <div className="flex-1 p-6 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
            <Terminal className="w-4 h-4 mr-2" />
            Live Logs
          </h3>
          <div className="flex items-center space-x-2">
            <select
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value as any)}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="all">All Levels</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="debug">Debug</option>
            </select>
            <button
              onClick={exportLogs}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Export logs"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="terminal terminal-dark rounded-lg p-4 flex-1 overflow-y-auto text-xs">
          {filteredLogs.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400 text-center py-8">
              {logs.length === 0 ? 'Waiting for execution to start...' : 'No logs match the current filter'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredLogs.map((log) => (
                <div key={log.id} className={`log-entry ${getLogColor(log.level)}`}>
                  [{formatTimestamp(log.timestamp)}] {log.level.toUpperCase()}: {log.message}
                </div>
              ))}
              {isExecuting && (
                <div className="text-blue-400 animate-pulse">
                  [{formatTimestamp(new Date())}] INFO: Execution in progress... ▊
                </div>
              )}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
        
        {/* Log Controls */}
        <div className="mt-3 flex items-center justify-between text-xs">
          <label className="flex items-center text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="mr-2 rounded"
            />
            Auto-scroll
          </label>
          <button
            onClick={() => {/* Clear logs functionality */}}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Clear
          </button>
        </div>
      </div>
      
      {/* Real-time Output Preview */}
      <div className="p-6 border-t border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
          <Eye className="w-4 h-4 mr-2" />
          Live Output
        </h3>
        <div className="card p-4 max-h-32 overflow-y-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Latest Agent Output:
          </div>
          <div className="text-sm text-gray-900 dark:text-white">
            {logs.length > 10 ? (
              <>
                <div className="mb-2 font-medium">Market Analysis Report - Executive Summary</div>
                <div className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  "The AI automation market shows unprecedented growth potential with a projected 47% year-over-year increase. Key findings indicate strong adoption in enterprise sectors, particularly in financial services and healthcare..."
                </div>
              </>
            ) : logs.length > 5 ? (
              <div className="text-xs text-gray-600 dark:text-gray-300">
                Research data collection in progress. Found 247 relevant data points from 15 sources...
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Waiting for agent output...
              </div>
            )}
          </div>
          {isExecuting && (
            <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
              <div className="status-indicator status-running mr-2"></div>
              <span>Updating in real-time</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MissionControl
